import { useState } from "react";
import { toast } from "sonner";
import api, { apiError, brl } from "@/lib/api";
import { Plus, Pencil, Trash2, CheckCircle2, Undo2, Landmark } from "lucide-react";
import usePaginatedList from "@/hooks/usePaginatedList";
import Pagination from "@/components/panel/Pagination";
import { ConfirmDialog } from "@/components/panel/ui";
import { Badge, Button, DataTable, SearchInput, Select } from "@/components/ui-kit";
import { AccountFormDialog, SettleDialog, DueFilter } from "./AccountDialogs";

const dt = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString("pt-BR") : "—");

/** O mesmo estado tem nomes diferentes conforme o sentido do dinheiro. */
const STATUS_LABEL = {
  quitada: { pagar: "Paga", receber: "Recebida" },
  cancelada: { pagar: "Cancelada", receber: "Cancelada" },
};

function Tile({ label, value, accent, hint }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl px-4 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`mt-1 font-mono text-lg font-semibold ${accent || "text-slate-900"}`}>{value}</p>
      {hint && <p className="text-xs text-red-500 mt-0.5">{hint}</p>}
    </div>
  );
}

/**
 * Contas a pagar e a receber.
 *
 * A tela de Financeiro só sabia somar o que JÁ tinha entrado. Isto é o outro
 * lado: o aluguel que vence dia 10, a nota do fornecedor, o cliente que levou a
 * cadeira e ficou de pagar. A ordenação é por vencimento — a pergunta que a tela
 * responde é "o que vence primeiro", não "o que foi cadastrado por último".
 */
export default function Accounts() {
  const [kind, setKind] = useState("");
  const [status, setStatus] = useState("aberta");
  const [vence, setVence] = useState("");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [settling, setSettling] = useState(null);
  const [removing, setRemoving] = useState(null);

  const ate = vence
    ? new Date(Date.now() + parseInt(vence, 10) * 86400000).toISOString().slice(0, 10)
    : undefined;

  const list = usePaginatedList("/admin/finance/entries", {
    kind: kind || undefined,
    status: status || undefined,
    ate,
  });
  const totals = list.meta?.totals;

  const reopen = async (e) => {
    try {
      await api.post(`/admin/finance/entries/${e.id}/reopen`);
      toast.success("Conta reaberta");
      list.refetch();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (e) => {
    try {
      await api.delete(`/admin/finance/entries/${e.id}`);
      toast.success("Conta excluída");
      list.refetch();
    } catch (err) { toast.error(apiError(err)); }
    finally { setRemoving(null); }
  };

  return (
    <div className="space-y-4" data-testid="finance-accounts">
      {/* O saldo previsto no meio, em destaque: é o número que responde "posso
          pagar tudo?" — os dois lados sozinhos não respondem. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="finance-accounts-totals">
        <Tile label="A pagar" value={brl(totals?.a_pagar ?? 0)} accent="text-red-600"
          hint={totals?.vencido_pagar ? `${brl(totals.vencido_pagar)} vencido` : null} />
        <Tile label="A receber" value={brl(totals?.a_receber ?? 0)} accent="text-emerald-700"
          hint={totals?.vencido_receber ? `${brl(totals.vencido_receber)} vencido` : null} />
        <Tile label="Saldo previsto" value={brl(totals?.saldo_previsto ?? 0)}
          accent={(totals?.saldo_previsto ?? 0) < 0 ? "text-red-600" : "text-slate-900"} />
        <Tile label="Já pago / recebido" value={`${brl(totals?.pago ?? 0)} / ${brl(totals?.recebido ?? 0)}`} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <SearchInput value={list.search} onChange={(e) => list.setSearch(e.target.value)}
          placeholder="Descrição ou categoria" aria-label="Buscar conta" width="w-56"
          data-testid="finance-entries-search" />
        <Select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="finance-entries-kind-filter"
          aria-label="Filtrar por tipo" className="w-auto px-2.5 text-slate-600">
          <option value="">A pagar e a receber</option>
          <option value="pagar">Só a pagar</option>
          <option value="receber">Só a receber</option>
        </Select>
        <Select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="finance-entries-status-filter"
          aria-label="Filtrar por situação" className="w-auto px-2.5 text-slate-600">
          <option value="aberta">Em aberto</option>
          <option value="atrasada">Atrasadas</option>
          <option value="quitada">Baixadas</option>
          <option value="cancelada">Canceladas</option>
          <option value="">Todas</option>
        </Select>
        <DueFilter value={vence} onChange={setVence} />
        <div className="flex-1" />
        <Button icon={Plus} onClick={() => setCreating(true)} data-testid="finance-entry-new">Lançar conta</Button>
      </div>

      <DataTable
        caption="Contas a pagar e a receber"
        rows={list.items}
        rowKey={(e) => e.id}
        loading={list.isLoading}
        error={list.isError}
        onRetry={list.refetch}
        empty={{
          icon: Landmark,
          title: "Nenhuma conta com esses filtros",
          desc: "Lance o aluguel, a nota do fornecedor ou uma venda a prazo para acompanhar aqui.",
          action: <Button icon={Plus} onClick={() => setCreating(true)}>Lançar a primeira conta</Button>,
          testId: "finance-entries-empty",
        }}
        columns={[
          {
            key: "vencimento", header: "Vence", width: "8rem",
            cell: (e) => (
              <>
                <p className={`font-mono text-sm ${e.overdue ? "text-red-600 font-semibold" : "text-slate-700"}`}>{dt(e.due_date)}</p>
                {e.overdue && <p className="text-[11px] text-red-500">em atraso</p>}
              </>
            ),
          },
          {
            key: "conta", header: "Conta",
            cell: (e) => (
              <>
                <p className="font-medium text-slate-900">{e.description}</p>
                <p className="text-xs text-slate-400">
                  {[e.category, e.supplier_name || e.customer_name].filter(Boolean).join(" · ") || "—"}
                </p>
              </>
            ),
          },
          {
            key: "tipo", header: "Tipo",
            cell: (e) => (e.kind === "pagar"
              ? <Badge tone="danger">A pagar</Badge>
              : <Badge tone="success">A receber</Badge>),
          },
          {
            key: "situacao", header: "Situação",
            cell: (e) => (e.status === "aberta"
              ? <Badge tone={e.overdue ? "warning" : "default"} dot={e.overdue}>Em aberto</Badge>
              : <Badge tone={e.status === "quitada" ? "success" : "default"}>
                  {STATUS_LABEL[e.status]?.[e.kind] || e.status}
                </Badge>),
          },
          {
            key: "valor", header: "Valor", align: "right", mono: true,
            cell: (e) => (
              <>
                <span className={`font-semibold ${e.kind === "pagar" ? "text-red-600" : "text-emerald-700"}`}>
                  {e.kind === "pagar" ? "− " : "+ "}{brl(e.amount)}
                </span>
                {/* Pago diferente do previsto é informação, não detalhe: é ela
                    que explica o caixa fechar diferente do planejado. */}
                {e.paid_amount != null && Math.abs(e.paid_amount - e.amount) >= 0.01 && (
                  <p className="text-xs text-slate-500">pago {brl(e.paid_amount)}</p>
                )}
              </>
            ),
          },
          {
            key: "acoes", header: "", align: "right", width: "8rem",
            cell: (e) => (
              <div className="flex justify-end gap-0.5">
                {e.status === "aberta" ? (
                  <Button variant="ghost" size="iconSm" onClick={() => setSettling(e)}
                    aria-label={`Baixar ${e.description}`} className="hover:text-emerald-600"
                    data-testid="finance-entry-settle"><CheckCircle2 size={15} /></Button>
                ) : e.status === "quitada" && (
                  <Button variant="ghost" size="iconSm" onClick={() => reopen(e)}
                    aria-label={`Reabrir ${e.description}`} data-testid="finance-entry-reopen"><Undo2 size={15} /></Button>
                )}
                <Button variant="ghost" size="iconSm" onClick={() => setEditing(e)}
                  aria-label={`Editar ${e.description}`} data-testid="finance-entry-edit"><Pencil size={15} /></Button>
                <Button variant="ghost" size="iconSm" onClick={() => setRemoving(e)}
                  aria-label={`Excluir ${e.description}`} className="hover:text-red-600"
                  data-testid="finance-entry-remove"><Trash2 size={15} /></Button>
              </div>
            ),
          },
        ]}
      />

      <Pagination page={list.page} lastPage={list.lastPage} total={list.total}
        onChange={list.setPage} label="contas" />

      {(creating || editing) && (
        <AccountFormDialog
          entry={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); list.refetch(); }}
        />
      )}
      {settling && (
        <SettleDialog entry={settling} onClose={() => setSettling(null)}
          onDone={() => { setSettling(null); list.refetch(); }} />
      )}
      <ConfirmDialog
        open={!!removing}
        onClose={() => setRemoving(null)}
        onConfirm={() => remove(removing)}
        title="Excluir conta"
        message={removing ? `Excluir "${removing.description}" de ${brl(removing.amount)}? Para manter o histórico, prefira cancelar.` : ""}
        confirmLabel="Excluir"
      />
    </div>
  );
}
